import { isDefined, isNotDefined } from '@sgme/fp';
import type { FormErrorSeverity } from 'models/formError';
import { createSelector } from 'reselect';
import { getAssociatedSalesDesk } from 'state/secondaryMarket/cpOrder/cpOrder.utils';
import type { SecondaryOrderStatePath } from 'state/secondaryMarket/secondaryMarket.model';
import { defaultsSelectorsFactory } from 'state/secondaryMarket/shared/defaults/defaults.selector';
import type { Form } from 'state/secondaryMarket/shared/form/form.model';
import { formInputSelectorsFactory } from 'state/secondaryMarket/shared/form/input/input.selector';
import { formOriginalSelectorsFactory } from 'state/secondaryMarket/shared/form/original/original.selector';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import {
  CLIENT_NOT_FOUND_BY_DOMINO_ERROR,
  MANDATORY_FIELDS_WARNING,
  MISSING_CASH_AMOUNT_OR_QUANTITY_ERROR,
  MISSING_CLIENT_ERROR,
  MISSING_CUSTODIAN_ERROR,
  MISSING_ELIOT_CODE_ERROR,
  MISSING_SALES_DESK_ERROR,
  MISSING_SALES_ERROR,
  MISSING_SETTLEMENT_DATE_ERROR,
  MISSING_SETTLEMENT_TYPE_ERROR,
  MISSING_TRADE_WAY_ERROR,
  NO_MODIFIED_FIELDS_WARNING,
  NO_SALES_FOR_CLIENT_ERROR,
  QUANTITY_NOT_DIVISIBLE_BY_LOT_SIZE,
  SALES_COVERAGE_LOAD_FAILURE,
} from 'state/secondaryMarket/shared/form/validationResults';
import type { Settlement } from 'state/secondaryMarket/shared/referential/referential.model';
import { referentialSelectorFactory } from 'state/secondaryMarket/shared/referential/referential.selector';
import { isClientSelected, toDefaultSettlementType } from 'state/secondaryMarket/shared/utils';
import { getAvailableSalesWithSelecteDesk } from 'state/secondaryMarket/utils';
import type { AppState } from 'state/state.model';

export type ValidationErrorSelector<F extends Form> = (
  appState: AppState,
  tabId: string,
) => readonly SecondaryMarketFormError<F>[];
type ExtraSelectors<F extends Form> = {
  getValidationErrors?: ValidationErrorSelector<F>;
  getAmendValidationErrors?: ValidationErrorSelector<F>;
};
const otherClientType = 'Other' as const;
const emptyArray: readonly [] = [];

const mapMessagesWithSeverity = (messages: string[], severity: FormErrorSeverity): SecondaryMarketFormError[] =>
  messages?.map(m => ({ fields: [], severity, message: m, values: {} }) satisfies SecondaryMarketFormError);
export function secondaryMarketSelectorsFactory<F extends Form, S extends ExtraSelectors<F>>(
  stateType: Exclude<SecondaryOrderStatePath, 'cpOrder'>,
  {
    getAmendValidationErrors: getExtraAmendValidationErrors,
    getValidationErrors: getExtraValidationErrors,
    ...extraSelectors
  }: S,
) {
  const inputFormSelectors = formInputSelectorsFactory(stateType, {});
  const defaultFormSelectors = defaultsSelectorsFactory(stateType);
  const originalFormselectors = formOriginalSelectorsFactory(stateType, {});
  const referentialSelectors = referentialSelectorFactory(stateType, {});
  const getFormClientType = (state: AppState, tabId: string) => {
    const inputClient = inputFormSelectors.getFormClientType(state, tabId);
    const defaultClient = defaultFormSelectors.getDefaultClientType(state, tabId);
    const originalClient = originalFormselectors.getFormClientType(state, tabId);
    return inputClient ?? defaultClient ?? originalClient ?? otherClientType;
  };
  const getFormCustodian = (state: AppState, tabId: string) => {
    const inputCustodian = inputFormSelectors.getFormCustodian(state, tabId);
    if (inputCustodian !== undefined) {
      return inputCustodian;
    }

    return originalFormselectors.getFormCustodian(state, tabId);
  };
  const getFormTradeWay = (state: AppState, tabId: string) =>
    inputFormSelectors.getFormTradeWay(state, tabId) ??
    defaultFormSelectors.getDefaultTradeWay(state, tabId) ??
    originalFormselectors.getFormTradeWay(state, tabId);

  const hasNoSelectedTradeWay = (state: AppState, tabId: string) => {
    const tradeWay = getFormTradeWay(state, tabId);
    return isNotDefined(tradeWay);
  };

  const getFormSalesId = (state: AppState, tabId: string) => {
    const salesId = inputFormSelectors.getFormSalesId(state, tabId);
    if (salesId !== undefined) {
      return salesId;
    }

    const defaultSalesId =
      defaultFormSelectors.getDefaultSalesId(state, tabId) ?? originalFormselectors.getFormSalesId(state, tabId);
    const selectedDesk = inputFormSelectors.getFormSalesDesk(state, tabId);
    if (selectedDesk === undefined) {
      return defaultSalesId;
    }
    const allSales = referentialSelectors.getSales(state, tabId);
    const availableSales = getAvailableSalesWithSelecteDesk(allSales, selectedDesk);
    if (availableSales.some(s => s.id === defaultSalesId)) {
      return defaultSalesId;
    }
    return undefined;
  };

  const getFormSettlement = (state: AppState, tabId: string): Settlement | undefined => {
    const settlementType =
      inputFormSelectors.getFormSettlementType(state, tabId) ??
      originalFormselectors.getFormSettlementType(state, tabId);
    const tradeWay = getFormTradeWay(state, tabId);
    const availableSettlementTypes = referentialSelectors.getSettlements(state, tabId);
    return settlementType ?? toDefaultSettlementType(availableSettlementTypes, tradeWay);
  };
  const getFormClient = (state: AppState, tabId: string) => {
    const inputClient = inputFormSelectors.getFormClient(state, tabId);
    const defaultClient = defaultFormSelectors.getDefaultClient(state, tabId);
    const originalClient = originalFormselectors.getFormClient(state, tabId);
    return inputClient ?? defaultClient ?? originalClient;
  };
  const getFormNeedCustodian = (appState: AppState, tabId: string) =>
    getFormClientType(appState, tabId) === otherClientType;
  const getFormSettlementDate = (state: AppState, tabId: string) =>
    inputFormSelectors.getFormSettlementDate(state, tabId) ??
    originalFormselectors.getFormSettlementDate(state, tabId) ??
    defaultFormSelectors.getDefaultSettlementDate(state, tabId);
  const getFormCustodianBdrId = (state: AppState, tabId: string) => {
    const inputCustodian = getFormCustodian(state, tabId)?.id;
    if (inputCustodian !== undefined) {
      return inputCustodian;
    }
    return originalFormselectors.getFormCustodian(state, tabId)?.id;
  };
  const getFormCountryId = (state: AppState, tabId: string, salesId: string | undefined) => {
    if (salesId === undefined) {
      return undefined;
    }
    const allSales = referentialSelectors.getSales(state, tabId);
    return allSales?.find(sales => sales.id === salesId)?.country;
  };
  const getFormSalesDesk = (state: AppState, tabId: string) => {
    const salesDesk =
      inputFormSelectors.getFormSalesDesk(state, tabId) ?? originalFormselectors.getFormSalesDesk(state, tabId);
    if (salesDesk !== undefined) {
      return salesDesk;
    }
    const salesId = getFormSalesId(state, tabId);
    const allSales = referentialSelectors.getSales(state, tabId);

    return getAssociatedSalesDesk(allSales, salesId);
  };
  const getFormQuantity = (state: AppState, tabId: string) =>
    inputFormSelectors.getFormQuantity(state, tabId) ?? originalFormselectors.getFormQuantity(state, tabId);
  const getFormCashAmount = (state: AppState, tabId: string) =>
    inputFormSelectors.getFormCashAmount(state, tabId) ?? originalFormselectors.getFormCashAmount(state, tabId);

  const getFormComment = (state: AppState, tabId: string) =>
    inputFormSelectors.getFormComment(state, tabId) ?? originalFormselectors.getFormComment(state, tabId);

  const canAdjustPrice = (state: AppState, tabId: string) =>
    getFormClientType(state, tabId) === 'Other' && getFormTradeWay(state, tabId) === 'ClientBuy';

  const getMaxQuantity = (state: AppState, tabId: string) =>
    getFormTradeWay(state, tabId) === 'ClientBuy'
      ? referentialSelectors.getMaxQuantitySell(state, tabId)
      : referentialSelectors.getMaxQuantityBuy(state, tabId);
  return {
    canAdjustPrice,
    getFormClient,
    getFormClientType,
    getFormNeedCustodian,
    getFormSettlementDate,
    getFormCustodianBdrId,
    getFormCustodian,
    getFormTradeWay,
    getFormSalesId,
    getFormCountryId,
    getFormSalesDesk,
    getFormSettlement,
    getFormQuantity,
    getFormCashAmount,
    getFormComment,
    getMaxQuantity,
    makeGetBackendReports: () =>
      createSelector(
        [
          referentialSelectors.getErrorMessages,
          referentialSelectors.getWarningMessages,
          referentialSelectors.getInfoMessages,
          referentialSelectors.getSettlementError,
        ],
        (errorMessages, warningMessages, infoMessages, settlementError): SecondaryMarketFormError<F>[] => {
          const errors = initErrors(errorMessages, warningMessages, infoMessages);
          if (settlementError) {
            return [...errors, settlementError];
          }
          return errors;
        },
      ),
    makeGetValidationErrors: () =>
      createSelector(
        [
          inputFormSelectors.getFormTradeSizeInputMode,
          getFormClient,
          getFormClientType,
          inputFormSelectors.getFormQuantity,
          inputFormSelectors.getFormCashAmount,
          getFormSalesId,
          getFormSalesDesk,
          getFormSettlementDate,
          getFormCustodianBdrId,
          getFormNeedCustodian,
          getFormSettlement,
          hasNoSelectedTradeWay,
          referentialSelectors.isSalesListEmpty,
          referentialSelectors.getClientLoadingStatus,
          referentialSelectors.hasFailedToLoadSalesFromDomino,
          referentialSelectors.getLotSize,
          getMaxQuantity,
          referentialSelectors.getMinQuantity,
          (state: AppState, tabId: string) => getExtraValidationErrors?.(state, tabId) ?? emptyArray,
        ],
        (
          mode,
          client,
          clientType,
          quantity,
          cashAmount,
          salesId,
          salesDesk,
          settlementDate,
          custodianId,
          needCustodian,
          settlement,
          hasNoSelectedTradeWay,
          isSalesListEmpty,
          clientLoadingStatus,
          hasFailedToLoadSalesFromDomino,
          lotSize,
          maxQuantity,
          minQuantity,
          additionalErrors,
        ): SecondaryMarketFormError<Form>[] => {
          const hasSelectedClient = isClientSelected(client, clientType);
          const errors = [...additionalErrors] as Readonly<SecondaryMarketFormError<Form>>[];
          if (!hasSelectedClient) {
            errors.push(MISSING_CLIENT_ERROR);
          }

          if (clientType === otherClientType) {
            if (needCustodian && !custodianId) {
              errors.push(MISSING_CUSTODIAN_ERROR);
            }
            if (hasFailedToLoadSalesFromDomino) {
              errors.push(CLIENT_NOT_FOUND_BY_DOMINO_ERROR);
            }
            if (clientLoadingStatus === 'error') {
              errors.push(SALES_COVERAGE_LOAD_FAILURE);
            }
            if (hasSelectedClient) {
              if (client?.eliotCode === undefined) {
                errors.push(MISSING_ELIOT_CODE_ERROR);
              }
              if (isSalesListEmpty) {
                errors.push(NO_SALES_FOR_CLIENT_ERROR);
              } else {
                if (salesDesk === undefined) {
                  errors.push(MISSING_SALES_DESK_ERROR);
                } else if (salesId === undefined) {
                  errors.push(MISSING_SALES_ERROR);
                }
              }
            }
          }
          if (mode === undefined || (quantity === undefined && cashAmount === undefined)) {
            errors.push(MISSING_CASH_AMOUNT_OR_QUANTITY_ERROR);
          }
          addQuantityInfoMessages(quantity, minQuantity, maxQuantity, lotSize, errors);

          if (settlementDate === undefined) {
            errors.push(MISSING_SETTLEMENT_DATE_ERROR);
          }

          if (settlement === undefined) {
            errors.push(MISSING_SETTLEMENT_TYPE_ERROR);
          }

          if (hasNoSelectedTradeWay) {
            errors.push(MISSING_TRADE_WAY_ERROR);
          }
          addMandatoryErrorIfNecessary(errors);

          return errors;
        },
      ),
    getAmendValidationErrors: createSelector(
      [
        inputFormSelectors.getFormChanges,
        getFormClient,
        getFormClientType,
        inputFormSelectors.getFormTradeSizeInputMode,
        getFormQuantity,
        getFormCashAmount,
        getFormSalesId,
        getFormSettlementDate,
        getFormCustodianBdrId,
        getFormNeedCustodian,
        getFormSettlement,
        referentialSelectors.hasFailedToLoadSalesFromDomino,
        referentialSelectors.getLotSize,
        getMaxQuantity,
        referentialSelectors.getMinQuantity,
        (state: AppState, tabId: string) => getExtraAmendValidationErrors?.(state, tabId) ?? emptyArray,
      ],
      (
        modified,
        client,
        clientType,
        mode,
        quantity,
        cashAmount,
        salesId,
        settlementDate,
        custodianId,
        needCustodian,
        settlement,
        hasFailedToLoadSalesFromDomino,
        lotSize,
        maxQuantity,
        minQuantity,
        additionalErrors,
      ): SecondaryMarketFormError<F>[] => {
        const errors: Readonly<SecondaryMarketFormError<F>>[] = [...additionalErrors];

        if (modified.length === 0) {
          errors.push(NO_MODIFIED_FIELDS_WARNING);
        }

        if (!isClientSelected(client, clientType)) {
          errors.push(MISSING_CLIENT_ERROR);
        }
        if (clientType === 'Other') {
          if (hasFailedToLoadSalesFromDomino) {
            errors.push(CLIENT_NOT_FOUND_BY_DOMINO_ERROR);
          }
          if (salesId === undefined) {
            errors.push(MISSING_SALES_ERROR);
          }
          if (needCustodian && !custodianId) {
            errors.push(MISSING_CUSTODIAN_ERROR);
          }
        }
        if (mode === undefined || (quantity === undefined && cashAmount === undefined)) {
          errors.push(MISSING_CASH_AMOUNT_OR_QUANTITY_ERROR);
        }
        if (settlementDate === undefined) {
          errors.push(MISSING_SETTLEMENT_DATE_ERROR);
        }
        if (settlement === undefined) {
          errors.push(MISSING_SETTLEMENT_TYPE_ERROR);
        }
        addQuantityInfoMessages(quantity, minQuantity, maxQuantity, lotSize, errors);
        addMandatoryErrorIfNecessary(errors);
        return errors;
      },
    ),
    ...extraSelectors,
  };
}

const addMandatoryErrorIfNecessary = <F extends Form>(errors: SecondaryMarketFormError<F>[]) => {
  if (errors.some(e => e.isMandatory)) {
    errors.push(MANDATORY_FIELDS_WARNING);
  }
};

function initErrors<F extends Form>(
  errorMessages: string[],
  warningMessages: string[],
  infoMessages: string[],
): SecondaryMarketFormError<F>[] {
  const transformedErrorMessages = mapMessagesWithSeverity(errorMessages, 'Failure');
  const transformedWarningMessages = mapMessagesWithSeverity(warningMessages, 'Error');
  const transformedInfoMessages = mapMessagesWithSeverity(infoMessages, 'Warning');
  return [...transformedErrorMessages, ...transformedWarningMessages, ...transformedInfoMessages];
}
export function addQuantityInfoMessages<F extends Form>(
  quantity: number | undefined,
  minQuantity: number | undefined,
  maxQuantity: number | undefined,
  lotSize: number | undefined,
  errors: SecondaryMarketFormError<F>[],
) {
  if (isDefined(quantity)) {
    if (isDefined(minQuantity) && isDefined(maxQuantity) && (quantity < minQuantity || quantity > maxQuantity)) {
      errors.push({
        fields: ['quantity'],
        severity: 'Warning',
        translationKey: 'quantityNotInRange',
        isMandatory: false,
        values: { min: minQuantity, max: maxQuantity },
      });
    }
    if (isDefined(lotSize)) {
      const dividedQuantity = quantity / lotSize;
      if (dividedQuantity % 1 !== 0) {
        errors.push(QUANTITY_NOT_DIVISIBLE_BY_LOT_SIZE);
      }
    }
  }
}
